/**
 * Constants and configuration data for the Energy Community Platform.
 *
 * This file contains ONLY static configuration -- dropdown options, asset type
 * definitions, dataset type schemas, and wizard step definitions.
 * All dynamic community/building/asset data now comes from the backend API
 * via React Query hooks.
 */

// Building Types Configuration (4 categories as per redesign)
export const buildingTypes = [
  {
    value: 'residential',
    label: 'Residential',
    description: 'Houses, apartments, multi-family dwellings'
  },
  {
    value: 'commercial',
    label: 'Commercial',
    description: 'Offices, shops, restaurants, hotels'
  },
  {
    value: 'industrial',
    label: 'Industrial',
    description: 'Factories, warehouses, manufacturing'
  },
  {
    value: 'municipal',
    label: 'Municipal',
    description: 'Public buildings, schools, hospitals, government'
  },
];

// Energy Classes (EU Standard)
export const energyClasses = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

// Building Field Configurations
export const buildingFields = [
  { name: 'building_id', label: 'Building ID', type: 'text', required: true },
  { name: 'building_type', label: 'Building Type', type: 'select', options: buildingTypes.map(t => t.value), required: true },
  { name: 'num_units', label: 'Number of Apartments/Units', type: 'number', unit: 'units' },
  { name: 'floor_area', label: 'Total Floor Area', type: 'number', unit: 'm2', required: true },
  { name: 'num_floors', label: 'Number of Floors', type: 'number', unit: 'floors' },
  { name: 'year_built', label: 'Year of Construction', type: 'number' },
  { name: 'energy_class', label: 'Energy Class', type: 'select', options: energyClasses },
  { name: 'has_rooftop_pv', label: 'Has Rooftop PV', type: 'checkbox' },
  { name: 'pv_asset_id', label: 'PV Asset ID', type: 'text', condition: 'has_rooftop_pv' },
  { name: 'has_battery', label: 'Has Battery Storage', type: 'checkbox' },
  { name: 'battery_asset_id', label: 'Battery Asset ID', type: 'text', condition: 'has_battery' },
  { name: 'has_ev_charger', label: 'Has EV Charger', type: 'checkbox' },
  { name: 'has_heat_pump', label: 'Has Heat Pump', type: 'checkbox' },
];

// Community Asset Types (shared assets for the entire community)
export const communityAssetTypes = {
  solar_pv_farm: {
    label: 'Solar PV Farm',
    icon: '☀️',
    color: '#FFB800',
    fields: [
      { name: 'capacity_kwp', label: 'Capacity (kWp)', type: 'number', unit: 'kWp', required: true },
      { name: 'efficiency', label: 'Efficiency', type: 'number', unit: '%', default: 18, required: true },
      { name: 'tilt_angle', label: 'Tilt Angle', type: 'number', unit: 'deg', default: 35 },
      { name: 'azimuth', label: 'Azimuth', type: 'number', unit: 'deg', default: 180 },
      { name: 'installation_date', label: 'Installation Date', type: 'date' },
    ]
  },
  wind_turbine: {
    label: 'Wind Turbine',
    icon: '💨',
    color: '#4A90E2',
    fields: [
      { name: 'capacity_kw', label: 'Capacity (kW)', type: 'number', unit: 'kW', required: true },
      { name: 'hub_height', label: 'Hub Height', type: 'number', unit: 'm', default: 50 },
      { name: 'rotor_diameter', label: 'Rotor Diameter', type: 'number', unit: 'm' },
      { name: 'cut_in_speed', label: 'Cut-in Speed', type: 'number', unit: 'm/s', default: 3 },
      { name: 'installation_date', label: 'Installation Date', type: 'date' },
    ]
  },
  community_battery: {
    label: 'Community Battery Storage',
    icon: '🔋',
    color: '#9B59B6',
    fields: [
      { name: 'capacity_kwh', label: 'Capacity (kWh)', type: 'number', unit: 'kWh', required: true },
      { name: 'p_charge_kw', label: 'Max Charge Power', type: 'number', unit: 'kW', required: true },
      { name: 'p_discharge_kw', label: 'Max Discharge Power', type: 'number', unit: 'kW', required: true },
      { name: 'efficiency_charge', label: 'Charge Efficiency', type: 'number', unit: '%', default: 95 },
      { name: 'efficiency_discharge', label: 'Discharge Efficiency', type: 'number', unit: '%', default: 95 },
      { name: 'soc_min', label: 'Min State of Charge', type: 'number', unit: '%', default: 10 },
      { name: 'soc_max', label: 'Max State of Charge', type: 'number', unit: '%', default: 90 },
    ]
  },
};

// Building Asset Types (per-building equipment)
export const buildingAssetTypes = {
  rooftop_pv: {
    label: 'Rooftop PV',
    icon: '☀️',
    color: '#FFB800',
    fields: [
      { name: 'capacity_kwp', label: 'Installed capacity (kWp)', type: 'number', unit: 'kWp', required: true },
      { name: 'tilt_angle', label: 'Panel tilt angle (degrees)', type: 'number', unit: 'deg', default: 30 },
      { name: 'azimuth', label: 'Panel azimuth (degrees)', type: 'number', unit: 'deg', default: 180 },
      { name: 'efficiency', label: 'Efficiency (%)', type: 'number', unit: '%', default: 85 },
    ]
  },
  battery: {
    label: 'Battery Storage',
    icon: '🔋',
    color: '#9B59B6',
    fields: [
      { name: 'capacity_kwh', label: 'Capacity (kWh)', type: 'number', unit: 'kWh', required: true },
      { name: 'p_charge_kw', label: 'Max charge power (kW)', type: 'number', unit: 'kW', required: true },
      { name: 'p_discharge_kw', label: 'Max discharge power (kW)', type: 'number', unit: 'kW', required: true },
      { name: 'round_trip_eff', label: 'Round-trip efficiency (%)', type: 'number', unit: '%', default: 90 },
      { name: 'initial_soc', label: 'Initial SoC (%)', type: 'number', unit: '%', default: 50 },
    ]
  },
  heat_pump: {
    label: 'Heat Pump',
    icon: '🌡️',
    color: '#E74C3C',
    fields: [
      { name: 'rated_power_kw', label: 'Rated power (kW)', type: 'number', unit: 'kW', required: true },
      { name: 'cop', label: 'COP (Coefficient of Performance)', type: 'number', default: 3.0 },
      { name: 'controllable', label: 'Controllable', type: 'select', options: ['yes', 'no'], default: 'yes' },
      { name: 't_min_c', label: 'Min Comfort Temperature', type: 'number', unit: '°C', default: 18 },
      { name: 't_max_c', label: 'Max Comfort Temperature', type: 'number', unit: '°C', default: 24 },
      { name: 't_initial_c', label: 'Initial Indoor Temperature', type: 'number', unit: '°C', default: 21 },
      { name: 'mode', label: 'Operating Mode', type: 'select', options: ['heating', 'cooling', 'both'], default: 'heating' },
      { name: 'thermal_mass_kwh_per_c', label: 'Thermal Mass', type: 'number', unit: 'kWh/°C', default: 2.0 },
    ]
  },
  hvac: {
    label: 'HVAC',
    icon: '❄️',
    color: '#3498DB',
    fields: [
      { name: 'rated_power_kw', label: 'Rated power (kW)', type: 'number', unit: 'kW', required: true },
      { name: 'controllable', label: 'Controllable', type: 'select', options: ['yes', 'no'], default: 'yes' },
      { name: 't_min_c', label: 'Min Comfort Temperature', type: 'number', unit: '°C', default: 18 },
      { name: 't_max_c', label: 'Max Comfort Temperature', type: 'number', unit: '°C', default: 26 },
      { name: 't_initial_c', label: 'Initial Indoor Temperature', type: 'number', unit: '°C', default: 22 },
      { name: 'mode', label: 'Operating Mode', type: 'select', options: ['heating', 'cooling', 'both'], default: 'both' },
      { name: 'cop_heating', label: 'COP (Heating)', type: 'number', default: 3.5 },
      { name: 'cop_cooling', label: 'EER (Cooling)', type: 'number', default: 3.0 },
      { name: 'thermal_mass_kwh_per_c', label: 'Thermal Mass', type: 'number', unit: 'kWh/°C', default: 2.0 },
    ]
  },
  ev_charger: {
    label: 'EV Charger',
    icon: '🔌',
    color: '#27AE60',
    fields: [
      { name: 'max_power_kw', label: 'Max charging power (kW)', type: 'number', unit: 'kW', default: 7.4, required: true },
      { name: 'num_evs', label: 'Number of EVs', type: 'number', required: true },
      { name: 'daily_energy_per_ev_kwh', label: 'Daily energy demand per EV (kWh)', type: 'number', unit: 'kWh', required: true },
      { name: 'controllable', label: 'Controllable (smart charging)', type: 'select', options: ['yes', 'no'], default: 'yes' },
      { name: 'arrival_hour', label: 'Typical Arrival Hour', type: 'number', unit: 'h (0-23)', default: 18 },
      { name: 'departure_hour', label: 'Typical Departure Hour', type: 'number', unit: 'h (0-23)', default: 7 },
      { name: 'battery_capacity_kwh', label: 'EV Battery Capacity', type: 'number', unit: 'kWh', default: 60 },
      { name: 'min_soc_pct', label: 'Min Departure SoC', type: 'number', unit: '%', default: 80 },
      { name: 'arrival_soc_pct', label: 'Typical Arrival SoC', type: 'number', unit: '%', default: 30 },
    ]
  },
};

// Keep old assetTypes for backward compatibility
export const assetTypes = communityAssetTypes;

// Community Dataset Types (for community assets)
export const communityDatasetTypes = [
  {
    type: 'pv_generation_profile',
    label: 'PV Generation Profile',
    icon: '☀️',
    description: 'Solar PV generation historical data for community solar farm',
    requiredColumns: ['timestamp', 'generation_kw'],
    format: 'CSV with hourly data, timestamps in ISO format',
    sampleData: [
      { timestamp: '2025-01-01T06:00:00Z', generation_kw: 0.5 },
      { timestamp: '2025-01-01T12:00:00Z', generation_kw: 105.6 },
      { timestamp: '2025-01-01T18:00:00Z', generation_kw: 12.3 },
    ]
  },
  {
    type: 'wind_generation_profile',
    label: 'Wind Generation Profile',
    icon: '💨',
    description: 'Wind turbine generation historical data',
    requiredColumns: ['timestamp', 'generation_kw'],
    format: 'CSV with hourly data, timestamps in ISO format',
    sampleData: [
      { timestamp: '2025-01-01T00:00:00Z', generation_kw: 45.2 },
      { timestamp: '2025-01-01T06:00:00Z', generation_kw: 78.5 },
      { timestamp: '2025-01-01T12:00:00Z', generation_kw: 92.1 },
    ]
  },
  {
    type: 'energy_tariff_data',
    label: 'Historical Import Tariff',
    icon: '💶',
    description: 'Historical time-of-use electricity import pricing (yearly dataset)',
    requiredColumns: ['timestamp', 'import_price_eur_kwh'],
    format: 'CSV with hourly tariff data (8760 rows for full year)',
    sampleData: [
      { timestamp: '2025-01-01T00:00:00Z', import_price_eur_kwh: 0.08 },
      { timestamp: '2025-01-01T07:00:00Z', import_price_eur_kwh: 0.12 },
      { timestamp: '2025-01-01T18:00:00Z', import_price_eur_kwh: 0.25 },
    ]
  },
  {
    type: 'feed_in_tariff_data',
    label: 'Historical Export Tariff',
    icon: '⚡',
    description: 'Historical electricity export/feed-in pricing (yearly dataset)',
    requiredColumns: ['timestamp', 'export_price_eur_kwh'],
    format: 'CSV with hourly tariff data (8760 rows for full year)',
    sampleData: [
      { timestamp: '2025-01-01T00:00:00Z', export_price_eur_kwh: 0.04 },
      { timestamp: '2025-01-01T07:00:00Z', export_price_eur_kwh: 0.06 },
      { timestamp: '2025-01-01T18:00:00Z', export_price_eur_kwh: 0.10 },
    ]
  },
  {
    type: 'day_ahead_import_tariff',
    label: 'Day-Ahead Import Tariff',
    icon: '📈',
    description: 'Day-ahead electricity import prices (24 hourly values for a specific date). Upload new CSV each day — values accumulate by date.',
    requiredColumns: ['timestamp', 'import_price_eur_kwh'],
    format: 'CSV with 24 rows, timestamps matching the target date',
    sampleData: [
      { timestamp: '2026-04-09T00:00:00Z', import_price_eur_kwh: 0.072 },
      { timestamp: '2026-04-09T08:00:00Z', import_price_eur_kwh: 0.145 },
      { timestamp: '2026-04-09T18:00:00Z', import_price_eur_kwh: 0.198 },
    ]
  },
  {
    type: 'day_ahead_export_tariff',
    label: 'Day-Ahead Export Tariff',
    icon: '📉',
    description: 'Day-ahead electricity export/feed-in prices (24 hourly values for a specific date). Upload new CSV each day — values accumulate by date.',
    requiredColumns: ['timestamp', 'export_price_eur_kwh'],
    format: 'CSV with 24 rows, timestamps matching the target date',
    sampleData: [
      { timestamp: '2026-04-09T00:00:00Z', export_price_eur_kwh: 0.035 },
      { timestamp: '2026-04-09T08:00:00Z', export_price_eur_kwh: 0.068 },
      { timestamp: '2026-04-09T18:00:00Z', export_price_eur_kwh: 0.092 },
    ]
  },
  {
    type: 'weather_solar',
    label: 'Solar Weather Data',
    icon: '🌤️',
    description: 'Irradiance and temperature for PV generation forecasting',
    requiredColumns: ['timestamp', 'ghi_w_m2'],
    optionalColumns: ['temperature_c', 'cloud_cover_pct', 'dni_w_m2'],
    format: 'CSV with hourly weather data',
    sampleData: [
      { timestamp: '2025-01-01T06:00:00Z', ghi_w_m2: 50, temperature_c: 5 },
      { timestamp: '2025-01-01T12:00:00Z', ghi_w_m2: 850, temperature_c: 15 },
      { timestamp: '2025-01-01T18:00:00Z', ghi_w_m2: 120, temperature_c: 12 },
    ]
  },
  {
    type: 'weather_wind',
    label: 'Wind Weather Data',
    icon: '💨',
    description: 'Wind speed and direction for wind generation forecasting',
    requiredColumns: ['timestamp', 'wind_speed_m_s'],
    optionalColumns: ['wind_direction_deg', 'temperature_c'],
    format: 'CSV with hourly weather data',
    sampleData: [
      { timestamp: '2025-01-01T00:00:00Z', wind_speed_m_s: 5.2, wind_direction_deg: 220 },
      { timestamp: '2025-01-01T06:00:00Z', wind_speed_m_s: 7.8, wind_direction_deg: 240 },
      { timestamp: '2025-01-01T12:00:00Z', wind_speed_m_s: 9.1, wind_direction_deg: 250 },
    ]
  },
];

// Building Dataset Types (for buildings)
export const buildingDatasetTypes = [
  {
    type: 'building_load_profile',
    label: 'Building Load Profile',
    icon: '📊',
    description: 'Hourly electricity consumption data for the building',
    requiredColumns: ['timestamp', 'consumption_kwh'],
    optionalColumns: ['peak_demand_kw'],
    format: 'CSV with hourly timestamps',
    sampleData: [
      { timestamp: '2025-01-01T00:00:00Z', consumption_kwh: 125.5 },
      { timestamp: '2025-01-01T01:00:00Z', consumption_kwh: 110.3 },
      { timestamp: '2025-01-01T02:00:00Z', consumption_kwh: 98.7 },
    ]
  },
  {
    type: 'pv_generation_profile',
    label: 'PV Generation Profile',
    icon: '☀️',
    description: 'Hourly solar PV generation data for the building\'s rooftop system',
    requiredColumns: ['timestamp', 'generation_kwh'],
    format: 'CSV with hourly timestamps',
    sampleData: [
      { timestamp: '2025-01-01T06:00:00Z', generation_kwh: 0.5 },
      { timestamp: '2025-01-01T12:00:00Z', generation_kwh: 15.8 },
      { timestamp: '2025-01-01T18:00:00Z', generation_kwh: 2.3 },
    ]
  },
  {
    type: 'building_energy_bill',
    label: 'Building Energy Bill',
    icon: '💶',
    description: 'Monthly or periodic energy billing data for cost analysis',
    requiredColumns: ['period_start', 'period_end', 'consumption_kwh', 'cost_eur'],
    optionalColumns: ['peak_demand_kw', 'rate_eur_per_kwh'],
    format: 'CSV with monthly periods',
    sampleData: [
      { period_start: '2025-01-01', period_end: '2025-01-31', consumption_kwh: 3500, cost_eur: 875 },
      { period_start: '2025-02-01', period_end: '2025-02-28', consumption_kwh: 3200, cost_eur: 800 },
    ]
  },
];

// Legacy datasetTypes kept for backward compatibility (DatasetUploadPage)
export const datasetTypes = [
  {
    type: 'load',
    label: 'Load Profile',
    icon: '📊',
    description: 'Historical energy consumption data',
    requiredColumns: ['timestamp', 'value'],
    optionalColumns: ['building_id', 'quality'],
    unit: 'kWh',
    exampleFile: 'load_profile_example.csv',
  },
  {
    type: 'pv_generation',
    label: 'PV Generation',
    icon: '☀️',
    description: 'Solar PV generation historical data',
    requiredColumns: ['timestamp', 'value'],
    optionalColumns: ['irradiance', 'temperature'],
    unit: 'kWh',
    exampleFile: 'pv_generation_example.csv',
  },
  {
    type: 'wind_generation',
    label: 'Wind Generation',
    icon: '💨',
    description: 'Wind turbine generation historical data',
    requiredColumns: ['timestamp', 'value'],
    optionalColumns: ['wind_speed', 'direction'],
    unit: 'kWh',
    exampleFile: 'wind_generation_example.csv',
  },
  {
    type: 'tariff',
    label: 'Electricity Tariff',
    icon: '💶',
    description: 'Time-of-use electricity pricing',
    requiredColumns: ['timestamp', 'import_price', 'export_price'],
    optionalColumns: ['demand_charge'],
    unit: 'EUR/kWh',
    exampleFile: 'tariff_example.csv',
  },
  {
    type: 'weather',
    label: 'Weather Data',
    icon: '🌤️',
    description: 'Historical weather conditions',
    requiredColumns: ['timestamp', 'temperature'],
    optionalColumns: ['irradiance', 'wind_speed', 'humidity'],
    unit: 'various',
    exampleFile: 'weather_example.csv',
  },
];

// Initialization Wizard Steps (3-step flow)
export const initializationSteps = [
  {
    step: 1,
    title: 'Community Information',
    icon: '🏘️',
    description: 'Basic details about your energy community',
    fields: ['name', 'country', 'location', 'timezone', 'currency', 'description'],
  },
  {
    step: 2,
    title: 'Assets & Buildings',
    icon: '⚡',
    description: 'Configure assets, buildings, and upload data',
    subsections: ['Assets', 'Buildings', 'Data Upload'],
  },
  {
    step: 3,
    title: 'Review & Complete',
    icon: '✅',
    description: 'Review and finalize your community setup',
  },
];

// Supported Countries (restricted list)
export const countries = [
  { value: 'Italy', label: 'Italy', timezone: 'Europe/Rome' },
  { value: 'France', label: 'France', timezone: 'Europe/Paris' },
  { value: 'Germany', label: 'Germany', timezone: 'Europe/Berlin' },
  { value: 'Czech Republic', label: 'Czech Republic', timezone: 'Europe/Prague', currency: 'CZK' },
  { value: 'Greece', label: 'Greece', timezone: 'Europe/Athens' },
];

// Supported Timezones (only for the 5 countries)
export const timezones = [
  { value: 'Europe/Rome', label: 'Europe/Rome (Italy)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (France)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (Germany)' },
  { value: 'Europe/Prague', label: 'Europe/Prague (Czech Republic)' },
  { value: 'Europe/Athens', label: 'Europe/Athens (Greece)' },
];

// Supported Currencies
export const currencies = [
  { value: 'EUR', label: 'Euro (\u20AC)', symbol: '\u20AC' },
  { value: 'CZK', label: 'Czech Koruna (K\u010D)', symbol: 'K\u010D' },
];

// Sample CSV Data for Preview (legacy - used by DatasetUploadPage)
export const sampleCSVData = {
  load: `timestamp,value,building_id
2025-01-01T00:00:00Z,125.5,Building-A
2025-01-01T01:00:00Z,110.3,Building-A
2025-01-01T02:00:00Z,98.7,Building-A
2025-01-01T03:00:00Z,92.1,Building-A
2025-01-01T04:00:00Z,88.4,Building-A`,

  pv_generation: `timestamp,value,irradiance
2025-01-01T06:00:00Z,0.5,50
2025-01-01T07:00:00Z,15.2,250
2025-01-01T08:00:00Z,45.8,550
2025-01-01T09:00:00Z,78.3,750
2025-01-01T10:00:00Z,105.6,850`,

  tariff: `timestamp,import_price,export_price
2025-01-01T00:00:00Z,0.08,0.04
2025-01-01T01:00:00Z,0.08,0.04
2025-01-01T02:00:00Z,0.08,0.04
2025-01-01T07:00:00Z,0.12,0.06
2025-01-01T18:00:00Z,0.25,0.10`,
};

export default {
  assetTypes,
  communityAssetTypes,
  buildingAssetTypes,
  buildingTypes,
  buildingFields,
  energyClasses,
  datasetTypes,
  communityDatasetTypes,
  buildingDatasetTypes,
  initializationSteps,
  countries,
  timezones,
  currencies,
  sampleCSVData,
};
